import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  IInspectionIpsativeUploadProps,
  IInspectionSaveProps,
  IPostProjectInspectionByIdProps,
  IPostSurveyAddQuestionsProps,
  IPostSurveyResultProps,
  IProjectSaveProps,
  IProjectState,
  IProjectUpdateProps,
  IProjectUserUploadProps,
  IPutGridSaveProps,
  SearchConditionKeys,
} from 'interface/project/projectInfo';
import { RootState } from 'store/rootReducer';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import axios, { AxiosResponse } from 'axios';
import { makeHeader, makeMultipartFormDataHeader } from 'libs/mindUtils';
import { handleSurveyAdditionFormat } from 'libs/projectUtils';

const initialState: IProjectState = {
  isInitial: true,
  isProjectCreate: false,
  projectListSearchCondition: {
    page: 0,
    size: 10,
    compName: '',
    adminName: '',
  },
  projectListSubSearchCondition: {
    order: 'desc',
    orderBy: 'id',
  },
  accountsListSearchCondition: {
    page: 0,
    size: 10,
    name: '',
    compName: '',
  },
  surveyListSearchCondition: {
    page: 0,
    size: 10,
  },
  tmpStepper1: {
    projectAdmins: [],
    tmpProjectAdmins: [],
  },
  projectUsersListSearchCondition: {
    page: 0,
    size: 10,
  },
  urlProjectId: '',
  urlPartCode: '',
  progressInfo: 0,
  surveyResultList: [],
  surveyInfoResultList: [],
  projectName: '',
};

const instance = axios.create({
  validateStatus(status) {
    return status >= 200 && status <= 500;
  },
});

export const postProjectSave = createAsyncThunk<AxiosResponse, IProjectSaveProps, { state: RootState }>(
  'project/postProjectSave',
  ({ projectType, projectName, projectStartAt, projectEndAt, compName, compAdminList }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {
      projectType,
      projectName,
      projectStartAt,
      projectEndAt,
      compName,
      compAdminList,
    };
    return instance.post(`${url}/api/project/save`, body, header);
  },
);

export const putProjectSave = createAsyncThunk<AxiosResponse, IProjectUpdateProps, { state: RootState }>(
  'project/putProjectSave',
  ({ projectType, projectName, projectStartAt, projectEndAt, compName, compAdminList, projectId }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {
      projectType,
      projectName,
      projectStartAt,
      projectEndAt,
      compName,
      compAdminList,
    };
    return instance.put(`${url}/api/project/${projectId}/save`, body, header);
  },
);

export const postProjectUserUpload = createAsyncThunk<AxiosResponse, IProjectUserUploadProps, { state: RootState }>(
  'project/postProjectUserUpload',
  ({ projectId, file, action = 'new' }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeMultipartFormDataHeader(loginInfo?.accessToken);
    const form = new FormData();
    form.append('file', file as File);

    return instance.post(`${url}/api/project/${projectId}/users/upload?mode=${action}`, form, header);
  },
);

export const postProjectSurvey = createAsyncThunk<
  AxiosResponse,
  { projectId: string; surveyId: string },
  { state: RootState }
>('project/postProjectSurvey', ({ projectId, surveyId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {};
  return instance.post(`${url}/api/project/${projectId}/survey/${surveyId}`, body, header);
});

export const postProjectSurveyAdditionSave = createAsyncThunk<
  AxiosResponse,
  { projectId: string; questions: IPostSurveyAddQuestionsProps[] },
  { state: RootState }
>('project/postProjectSurveyAdditionSave', ({ projectId, questions }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const tmp = handleSurveyAdditionFormat(questions);
  return instance.post(`${url}/api/survey/${projectId}/addition/save`, tmp, header);
});

export const postInspectionSave = createAsyncThunk<AxiosResponse, IInspectionSaveProps, { state: RootState }>(
  'project/postInspectionSave',
  ({ inspectionName, inspectionDesc, saveFileName, grid }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {
      inspectionName,
      inspectionDesc,
      saveFileName,
      grid,
    };
    return instance.post(`${url}/api/inspection/save`, body, header);
  },
);

export const postInspectionIpsativeUpload = createAsyncThunk<
  AxiosResponse,
  IInspectionIpsativeUploadProps,
  { state: RootState }
>('project/postInspectionIpsativeUpload', ({ inspectionId, file }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeMultipartFormDataHeader(loginInfo?.accessToken);
  const form = new FormData();
  form.append('file', file as File);
  return instance.post(`${url}/api/inspection/${inspectionId}/ipsative/upload`, form, header);
});

export const postProjectInspectionById = createAsyncThunk<
  AxiosResponse,
  IPostProjectInspectionByIdProps,
  { state: RootState }
>('project/postProjectInspectionById', ({ projectId, inspectionId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.post(`${url}/api/project/${projectId}/inspection/${inspectionId}`, null, header);
});

export const putIpsativeGridSave = createAsyncThunk<AxiosResponse, IPutGridSaveProps, { state: RootState }>(
  'project/putIpsativeGridSave',
  ({ inspectionId, grid }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {
      grid,
    };
    return instance.put(`${url}/api/inspection/${inspectionId}/ipsative/grid/save`, body, header);
  },
);

export const postUsersPartCode = createAsyncThunk<AxiosResponse, { projectId: string }, { state: RootState }>(
  'project/postUsersPartCode',
  ({ projectId }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {};
    return instance.post(`${url}/api/project/${projectId}/users/part-code`, body, header);
  },
);

export const postUserSave = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    userName: string;
    userEmail: string;
    userCompName: string;
    userDept: string;
    userPosition: string;
  },
  { state: RootState }
>('project/postUserSave', ({ projectId, userName, userCompName, userDept, userPosition, userEmail }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    userName,
    userEmail,
    userCompName,
    userDept,
    userPosition,
  };
  return instance.post(`${url}/api/project/${projectId}/user/save`, body, header);
});

export const putUserSave = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    userId: string;
    userName: string;
    userEmail: string;
    userCompName: string;
    userDept: string;
    userPosition: string;
  },
  { state: RootState }
>(
  'project/putUserSave',
  ({ projectId, userId, userName, userCompName, userDept, userPosition, userEmail }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body = {
      userName,
      userEmail,
      userCompName,
      userDept,
      userPosition,
    };
    return instance.put(`${url}/api/project/${projectId}/user/${userId}/save`, body, header);
  },
);

export const getUserSendMailAction = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    userId: string;
  },
  { state: RootState }
>('project/getUserSendMailAction', ({ projectId, userId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/project/${projectId}/sendmail/${userId}`, header);
});

export const getUserSendMultiMailAction = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    users: string[];
  },
  { state: RootState }
>('project/getUserSendMailAction', ({ projectId, users }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    users,
  };
  return instance.post(`${url}/api/project/${projectId}/multi/sendmail`, body, header);
});

export const postUserSendMailSendOptionAction = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    option: 'ALL' | 'NOT' | 'INCOMPLETE';
  },
  { state: RootState }
>('project/postUserSendMailSendOptionAction', ({ projectId, option }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {};
  return instance.post(`${url}/api/project/${projectId}/sendmail?send=${option}`, body, header);
});

export const putProjectStateSave = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    projectState: 'PD' | 'PR' | 'CP';
  },
  { state: RootState }
>('project/putProjectStateSave', ({ projectId, projectState }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    projectState,
  };
  return instance.put(`${url}/api/project/${projectId}/state/save`, body, header);
});

// 프로젝트 진단도구(서베이) 검사결과 다운로드
export const getProjectSurveyResultDownload = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
  },
  { state: RootState }
>('project/getProjectSurveyResultDownload', ({ projectId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/survey/result/${projectId}/download`, {
    ...header,
    responseType: 'blob',
  });
});

// 프로젝트 자료수집 검사결과 다운로드
export const getProjectResultDownload = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
  },
  { state: RootState }
>('project/getProjectResultDownload', ({ projectId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/project/${projectId}/result/download`, {
    ...header,
    responseType: 'blob',
  });
});

// 미실시자 엑셀 다운로드
export const getProjectIncompleteUsersExcelDownload = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
  },
  { state: RootState }
>('project/getProjectIncompleteUsersExcelDownload', ({ projectId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/project/${projectId}/users/excel/download/external`, {
    ...header,
    responseType: 'blob',
  });
});

// 진단결과 요약 CSV 다운로드
export const getProjectCsvExport = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
  },
  { state: RootState }
>('project/getProjectCsvExport', ({ projectId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/csv/export/project/${projectId}`, {
    ...header,
    responseType: 'blob',
    timeout: 2400000, // 40분 (30분 이상 소요 가능하므로 여유있게 설정)
  });
});

// 개별 진단 결과 PDF 다운로드
export const getIndividualPdfResultDownload = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    partCode: string;
  },
  { state: RootState }
>('project/getIndividualPdfResultDownload', ({ projectId, partCode }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const hardcodedId = 'b64a9678-10d7-4624-9392-cdd7b0c8cb8b';
  return instance.get(`${url}/api/pdf/result/${projectId}/${hardcodedId}/${partCode}`, {
    ...header,
    responseType: 'blob',
  });
});

// 관리자 권한 매핑
export const postAdminRoleOneMapping = createAsyncThunk<
  AxiosResponse,
  {
    adminId: string;
    roleId: number;
  },
  { state: RootState }
>('project/postAdminRoleOneMapping', ({ adminId, roleId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    adminId,
    roleId,
  };
  return instance.post(`${url}/api/admin/role/onemapping`, body, header);
});

// 진단도구 저장
export const postSurveySave = createAsyncThunk<
  AxiosResponse,
  {
    surveyName: string;
    saveFileName: string;
    surveyDesc: string;
    surveyType: string;
  },
  { state: RootState }
>('project/postSurveySave', ({ surveyName, saveFileName, surveyDesc, surveyType }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    surveyName,
    saveFileName,
    surveyDesc,
    surveyType,
  };
  return instance.post(`${url}/api/survey/save`, body, header);
});

// 진단도구 정보(질문) 엑셀 업로드
export const postSurveyQuestionUpload = createAsyncThunk<
  AxiosResponse,
  {
    surveyId: string;
    file: File;
  },
  { state: RootState }
>('project/postSurveySave', ({ surveyId, file }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeMultipartFormDataHeader(loginInfo?.accessToken);
  const form = new FormData();
  form.append('file', file as File);
  return instance.post(`${url}/api/survey/${surveyId}/questions/upload`, form, header);
});

// 참여자 삭제
export const deleteUserSave = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    userId: string;
  },
  { state: RootState }
>('project/deleteUserSave', ({ projectId, userId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.delete(`${url}/api/project/${projectId}/user/${userId}`, header);
});

// 프로젝트 삭제
export const deleteProject = createAsyncThunk<AxiosResponse, { projectId: string }, { state: RootState }>(
  'project/deleteProject',
  ({ projectId }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    return instance.delete(`${url}/api/project/${projectId}`, header);
  },
);

// 계정관리
// 계정정보 수정
export const putAdminInfo = createAsyncThunk<
  AxiosResponse,
  {
    adminId: string;
    mobile: string;
    compName: string;
    compRole: string;
  },
  { state: RootState }
>('project/putAdminInfo', ({ adminId, mobile, compName, compRole }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    mobile,
    compName,
    compRole,
  };
  return instance.put(`${url}/api/comp/biz/admin/${adminId}/info`, body, header);
});

// 계정 비밀번호 수정
export const putAdminPassword = createAsyncThunk<
  AxiosResponse,
  {
    adminId: string;
    password: string;
    newpassword: string;
  },
  { state: RootState }
>('project/putAdminPassword', ({ adminId, password, newpassword }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    password,
    newpassword,
  };
  return instance.put(`${url}/api/comp/biz/admin/${adminId}/password`, body, header);
});

// 개별진단 프로젝트 안내문
export const postGuideSave = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
    des: string;
    agreement: string;
    complete: string;
    emailTitle: string;
    emailBody: string;
  },
  { state: RootState }
>('project/postGuideSave', ({ projectId, des, agreement, complete, emailTitle, emailBody }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  const body = {
    projectId,
    des,
    agreement,
    complete,
    emailTitle,
    emailBody,
  };
  return instance.post(`${url}/api/guide/save`, body, header);
});

export const getGuideDetail = createAsyncThunk<
  AxiosResponse,
  {
    projectId: string;
  },
  { state: RootState }
>('project/getGuideDetail', ({ projectId }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  return instance.get(`${url}/api/guide/${projectId}/detail`, header);
});

export const postSurveyResult = createAsyncThunk<AxiosResponse, IPostSurveyResultProps, { state: RootState }>(
  'project/postSurveyResult',
  ({ projectId, partCode, surveyResultList, surveyAddResultList }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body: Omit<IPostSurveyResultProps, 'projectId'> = {
      partCode,
      surveyResultList,
      surveyAddResultList,
    };
    return instance.post(`${url}/api/result/survey/${projectId}/save`, body, header);
  },
);

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    changeProjectListSearchCondition(state, action) {
      const { projectListSearchCondition } = action.payload;
      state.projectListSearchCondition = projectListSearchCondition;
      if (isEqual(omit(initialState.projectListSearchCondition, []), omit(projectListSearchCondition, []))) {
        state.isInitial = true;
      } else {
        state.isInitial = false;
      }
    },
    changeProjectListSubSearchCondition(state, action) {
      const { order, orderBy } = action.payload;
      state.projectListSubSearchCondition.order = order;
      state.projectListSubSearchCondition.orderBy = orderBy;
    },
    changePageAndSize(state, action) {
      const { page, size } = action.payload;
      state.projectListSearchCondition.page = page;
      state.projectListSearchCondition.size = size;
    },
    changeAccountsListSearchCondition(state, action) {
      const { accountsListSearchCondition } = action.payload;
      state.accountsListSearchCondition = accountsListSearchCondition;
      if (isEqual(omit(initialState.accountsListSearchCondition, []), omit(accountsListSearchCondition, []))) {
        state.isInitial = true;
      } else {
        state.isInitial = false;
      }
    },
    changeAccountsPageAndSize(state, action) {
      const { page, size } = action.payload;
      state.accountsListSearchCondition.page = page;
      state.accountsListSearchCondition.size = size;
    },
    changeProjectCreateStatus(state, action) {
      const { isProjectCreate } = action.payload;
      state.isProjectCreate = isProjectCreate;
    },
    changeStepper1Value(state, action) {
      state.tmpStepper1 = {
        ...state.tmpStepper1,
        ...action.payload,
      };
    },
    changeCmnPageAndSize(state, action) {
      const { key, page, size } = action.payload as { key: SearchConditionKeys; page: number; size: number };
      if (state[key]) {
        state[key].page = page;
        state[key].size = size;
      }
    },
    changeUrlProjectId: (state, action) => {
      const { urlProjectId } = action.payload;
      state.urlProjectId = urlProjectId;
    },
    changeUrlPartCode: (state, action) => {
      const { urlPartCode } = action.payload;
      state.urlPartCode = urlPartCode;
    },
    changeProgressInfo: (state, action) => {
      const { progressInfo } = action.payload;
      state.progressInfo = progressInfo;
    },
    changeSurveyResult: (state, action) => {
      const { surveyId, answerResult, choiceResultList } = action.payload;
      const findCheck = state.surveyResultList.findIndex(obj => obj.surveyId === surveyId);
      const copyArray = [...state.surveyResultList];
      if (findCheck !== -1) {
        copyArray[findCheck] = { surveyId, answerResult, choiceResultList };
        state.surveyResultList = copyArray;
      } else {
        state.surveyResultList = [
          ...state.surveyResultList,
          {
            surveyId,
            answerResult,
            choiceResultList,
          },
        ];
      }
    },
    changeSurveyInfoResult: (state, action) => {
      const { surveyId, answerResult, choiceResultList } = action.payload;
      const findCheck = state.surveyInfoResultList.findIndex(obj => obj.surveyId === surveyId);
      const copyArray = [...state.surveyInfoResultList];
      if (findCheck !== -1) {
        copyArray[findCheck] = { surveyId, answerResult, choiceResultList };
        state.surveyInfoResultList = copyArray;
      } else {
        state.surveyInfoResultList = [
          ...state.surveyInfoResultList,
          {
            surveyId,
            answerResult,
            choiceResultList,
          },
        ];
      }
    },
    changeProjectName: (state, action) => {
      const { projectName } = action.payload;
      state.projectName = projectName;
    },
    resetOtherOption: state => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { urlProjectId, urlPartCode, ...rest } = initialState;
      return { ...state, ...rest };
    },
  },
});

export const {
  changeProjectListSearchCondition,
  changePageAndSize,
  changeProjectListSubSearchCondition,
  changeAccountsListSearchCondition,
  changeAccountsPageAndSize,
  changeProjectCreateStatus,
  changeStepper1Value,
  changeCmnPageAndSize,
  changeUrlProjectId,
  changeUrlPartCode,
  changeProgressInfo,
  changeSurveyResult,
  changeProjectName,
  changeSurveyInfoResult,
  resetOtherOption,
} = projectSlice.actions;
export default projectSlice.reducer;
export const projectSelector = (state: RootState): IProjectState => state.project;
